/**
 * vd-reveal.js — lightweight scroll-reveal for `.vd-reveal` elements.
 *
 * Content is visible by default (see velodream.css) and only becomes
 * hidden once this script confirms it is running, by adding
 * `vd-reveal-js` to <html>. That way, if this script never loads or
 * errors, nothing is ever hidden — the CSS gate requires the class this
 * script adds, not the reverse.
 */

/**
 * Measures the combined flow height of the announcement bar + header
 * section group — both are wrapped individually by Shopify's
 * `{% sections 'header-group' %}` tag with the shared class
 * `.shopify-section-group-header-group` (see theme.liquid) — and writes
 * it to `--vd-header-stack` on <html>.
 *
 * .vd-hero (velodream.css) subtracts this from `100dvh` because the
 * header stack renders ABOVE the hero in normal document flow (the
 * sticky header in header.liquid still occupies its flow height even
 * when transparent/overlaying, since `position: sticky` never removes
 * an element from flow). Without subtracting it, a `100dvh` hero with
 * bottom-anchored content gets pushed below the fold by exactly the
 * header stack's height on short viewports.
 *
 * Defaults to 0px (current/no-JS behavior) if the selector is missing
 * or this script never runs — see the `560px` / `100dvh` fallback
 * chain on `.vd-hero` in velodream.css.
 */
function updateHeaderStackHeight() {
  var stackEls = document.querySelectorAll('.shopify-section-group-header-group');
  var stackHeight = 0;

  stackEls.forEach(function (el) {
    stackHeight += el.offsetHeight;
  });

  document.documentElement.style.setProperty('--vd-header-stack', stackHeight + 'px');
}

document.addEventListener('DOMContentLoaded', function () {
  updateHeaderStackHeight();

  var lastWidth = window.innerWidth;
  window.addEventListener('resize', function () {
    var currentWidth = window.innerWidth;
    // Ignore height-only resizes (e.g. mobile browser chrome
    // showing/hiding on scroll) — only width changes can change the
    // header stack's wrapped height.
    if (currentWidth === lastWidth) return;
    lastWidth = currentWidth;
    updateHeaderStackHeight();
  });
});

/**
 * Returns the nearest ancestor that scrolls horizontally, or null.
 *
 * A `.vd-reveal` element inside a horizontal scroller (the category-tile
 * carousel) may sit outside the VIEWPORT horizontally while its scroller
 * is perfectly in view — at 375px only the first two of five tiles fall
 * within the viewport's width. IntersectionObserver against the default
 * root therefore never fires for the rest, leaving them stuck at
 * `opacity: 0`. That silently kills the carousel's peek affordance: the
 * third tile's 44% flex-basis exists precisely so a sliver shows and
 * signals "this scrolls", and an unrevealed tile renders as blank page.
 */
function horizontalScrollerFor(el) {
  var node = el.parentElement;

  while (node && node !== document.body) {
    var overflowX = window.getComputedStyle(node).overflowX;
    if (overflowX === 'auto' || overflowX === 'scroll') return node;
    node = node.parentElement;
  }

  return null;
}

document.addEventListener('DOMContentLoaded', function () {
  document.documentElement.classList.add('vd-reveal-js');

  var targets = document.querySelectorAll('.vd-reveal');
  if (!targets.length) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (reduceMotion || typeof IntersectionObserver === 'undefined') {
    targets.forEach(function (el) {
      el.classList.add('is-revealed');
    });
    return;
  }

  var observer = new IntersectionObserver(
    function (entries, obs) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;

        // For a scroller, reveal every member of its group at once; the
        // per-element `--vd-reveal-delay` still staggers them visually.
        // For a plain target, `members` is the element itself.
        var members = entry.target.__vdRevealGroup || [entry.target];
        members.forEach(function (el) {
          el.classList.add('is-revealed');
        });

        obs.unobserve(entry.target);
      });
    },
    { threshold: 0.15, rootMargin: '0px 0px -40px' }
  );

  // Group targets by their horizontal scroller so a scroller is observed
  // once, on behalf of all its children. Vertical position is what should
  // gate a reveal; horizontal position within a carousel should not.
  var scrollerGroups = new Map();

  targets.forEach(function (el) {
    var scroller = horizontalScrollerFor(el);

    if (!scroller) {
      observer.observe(el);
      return;
    }

    if (!scrollerGroups.has(scroller)) scrollerGroups.set(scroller, []);
    scrollerGroups.get(scroller).push(el);
  });

  scrollerGroups.forEach(function (members, scroller) {
    scroller.__vdRevealGroup = members;
    observer.observe(scroller);
  });
});
